import React, { useState, useEffect, useRef } from 'react';
import checkCircleIcon from '../assets/icons/check_circle.png';
import dropletIcon from '../assets/icons/Droplet.png';
import logoCircleReverse from '../assets/images/andonx-logo.png';

const PrimaryTicketDisplay = ({ ticket, activeTickets = [], currentSlideIndex = 0, apiKey, environment, apiResponse, onPartDetailsUpdate }) => {
  const [timerValue, setTimerValue] = useState('--:--:--');
  const [currentTime, setCurrentTime] = useState('');
  const [isNegative, setIsNegative] = useState(false);
  const [displayTicket, setDisplayTicket] = useState(ticket);
  const [frozenTimer, setFrozenTimer] = useState(null);
  const trackRef = useRef(null);
  const prevSlideIndexRef = useRef(currentSlideIndex);
  const isTransitioningRef = useRef(false);

  // Update displayTicket when ticket prop changes
  useEffect(() => {
    if (ticket && (!displayTicket || ticket.ticket_id !== displayTicket.ticket_id)) {
      setDisplayTicket(ticket);
    }
  }, [ticket?.ticket_id]);

  // Initialize carousel position on mount or when slides change
  useEffect(() => {
    if (trackRef.current && activeTickets && activeTickets.length > 1) {
      const track = trackRef.current;
      const container = track.parentElement;
      const slideWidth = container ? container.offsetWidth : 0;

      // Initialize to position 1 (first real slide, after cloned last slide)
      track.style.transition = 'none';
      track.style.transform = `translateX(${-slideWidth}px)`;
      // Force reflow
      void track.offsetHeight;
    }
  }, [activeTickets]);

  // Sync carousel with currentSlideIndex
  useEffect(() => {
    if (trackRef.current && activeTickets && activeTickets.length > 0 && typeof currentSlideIndex === 'number') {
      const track = trackRef.current;
      const container = track.parentElement;
      const slideCount = activeTickets.length;

      if (slideCount === 0) return;

      if (slideCount === 1) {
        // Single slide - no animation needed
        track.style.transform = 'translateX(0)';
        track.style.transition = 'none';
        prevSlideIndexRef.current = currentSlideIndex;
        return;
      }

      const prevIndex = prevSlideIndexRef.current;
      // Each slide is 100% of container width
      const slideWidth = container ? container.offsetWidth : 0;

      // For infinite loop: we have [lastClone, ...all, firstClone]
      // Real slides are at indices 1 to slideCount
      // We start at position 1 (first real slide) instead of 0
      const realSlideIndex = currentSlideIndex + 1; // Offset by 1 to account for cloned last slide

      // Calculate the translateX value for the current slide
      // Negative values slide left (right-to-left direction)
      const translateX = -realSlideIndex * slideWidth;

      // Check if we're wrapping from last to first (infinite loop)
      const isWrappingForward = prevIndex === slideCount - 1 && currentSlideIndex === 0;

      if (isWrappingForward) {
        // When wrapping, first animate to the cloned first slide (at the end)
        // This is at index slideCount + 1 (last clone is at slideCount, first clone is at slideCount + 1)
        const clonedFirstIndex = slideCount + 1;
        const wrappedTranslateX = -clonedFirstIndex * slideWidth;

        // Apply transition to cloned slide
        track.style.transition = 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)';
        track.style.transform = `translateX(${wrappedTranslateX}px)`;

        // After animation completes, instantly reset to position 1 (real first slide)
        const handleTransitionEnd = () => {
          if (currentSlideIndex === 0) {
            // Reset to real first slide without animation for seamless loop
            track.style.transition = 'none';
            track.style.transform = `translateX(${-slideWidth}px)`; // Position 1 (real first slide)
            // Force reflow
            void track.offsetHeight;
            // Re-enable transitions for next slide
            track.style.transition = 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)';
          }
          track.removeEventListener('transitionend', handleTransitionEnd);
        };

        track.addEventListener('transitionend', handleTransitionEnd, { once: true });
      } else {
        // Normal navigation - animate to the target slide
        track.style.transition = 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)';
        track.style.transform = `translateX(${translateX}px)`;
      }

      prevSlideIndexRef.current = currentSlideIndex;
    }
  }, [currentSlideIndex, activeTickets]);

  // Update displayTicket when currentSlideIndex changes
  useEffect(() => {
    if (activeTickets && activeTickets.length > 0 && typeof currentSlideIndex === 'number') {
      const actualIndex = currentSlideIndex % activeTickets.length;
      if (activeTickets[actualIndex]) {
        setDisplayTicket(activeTickets[actualIndex]);
      }
    }
  }, [currentSlideIndex, activeTickets]);

  // Part details are now fetched from database, no need for API calls

  // Helper function to check if ticket is at stage 4 (completion)
  const isStage4Completed = (ticket) => {
    if (!ticket) return false;

    // Check if ticket has events and get the latest status
    if (Array.isArray(ticket.events) && ticket.events.length > 0) {
      const latestEvent = ticket.events.reduce((a, b) => (new Date(a.event_date) > new Date(b.event_date) ? a : b));
      const currentStatus = latestEvent.status?.toLowerCase();
      return currentStatus === 'resolved' || !ticket.is_active;
    }

    // Fallback to ticket status
    return ticket.status?.toLowerCase() === 'resolved' || !ticket.is_active;
  };

  // Helper function to get completion time (when ticket transitioned from alert to non-alert state)
  const getCompletionTime = (ticket) => {
    if (!ticket || !Array.isArray(ticket.events) || ticket.events.length === 0) {
      return null;
    }

    // Sort events by date to find the transition
    const sortedEvents = [...ticket.events].sort((a, b) => new Date(a.event_date) - new Date(b.event_date));

    // Find the last alert state event and the first non-alert state event
    const alertStates = ['alerted', 'acknowledged', 'evaluated'];
    let lastAlertEvent = null;
    let firstNonAlertEvent = null;

    for (const event of sortedEvents) {
      const status = event.status?.toLowerCase();
      if (alertStates.includes(status)) {
        lastAlertEvent = event;
      } else if (!alertStates.includes(status) && !firstNonAlertEvent) {
        firstNonAlertEvent = event;
        break; // Found the transition point
      }
    }

    // If we found a non-alert event, use its timestamp as completion time
    if (firstNonAlertEvent) {
      return new Date(firstNonAlertEvent.event_date);
    }

    // Fallback to ticket timestamp if no clear transition found
    return new Date(ticket.timestamp);
  };

  useEffect(() => {
    if (!displayTicket) return; // Prevent running effect if ticket is null

    // Removed excessive logging - ticket data logging was causing console spam

    // Update current time
    const updateCurrentTime = () => {
      const now = new Date();
      const pacificTime = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(now);
      setCurrentTime(pacificTime);
    };

    // Update timer
    const updateTimer = () => {
      // Check if this ticket is at stage 4 completion
      if (isStage4Completed(displayTicket)) {
        // If we have a frozen timer value, use it
        if (frozenTimer) {
          setTimerValue(frozenTimer.value);
          setIsNegative(frozenTimer.isNegative);
          return;
        }

        // Calculate and freeze the timer value at the moment of completion
        // The timer should count down from 10 minutes from when the ticket was first opened (alerted)
        let ticketOpenTime = null;

        if (displayTicket && Array.isArray(displayTicket.events) && displayTicket.events.length > 0) {
          // Find the first event (alerted) to get the ticket opening time
          const firstEvent = displayTicket.events.reduce((a, b) => (new Date(a.event_date) < new Date(b.event_date) ? a : b));
          ticketOpenTime = new Date(firstEvent.event_date);
        } else {
          // Fallback to ticket timestamp if no events
          ticketOpenTime = new Date(displayTicket.timestamp);
        }

        if (ticketOpenTime) {
          // Calculate target time as 10 minutes from when ticket was opened
          const targetTime = new Date(ticketOpenTime.getTime() + 10 * 60 * 1000);

          // Get the completion time (when ticket transitioned from alert to non-alert state)
          const completionTime = getCompletionTime(displayTicket);

          if (completionTime) {
            const diffSeconds = Math.floor((targetTime - completionTime) / 1000);
            const negative = diffSeconds < 0;
            const absSeconds = Math.abs(diffSeconds);

            const hours = Math.floor(absSeconds / 3600);
            const minutes = Math.floor((absSeconds % 3600) / 60);
            const seconds = absSeconds % 60;

            const sign = negative ? '-' : '';
            const timeStr = `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

            // Store the frozen timer value
            setFrozenTimer({
              value: timeStr,
              isNegative: negative
            });

            setTimerValue(timeStr);
            setIsNegative(negative);
          }
        }
        return;
      }

      // For active tickets (not stage 4), calculate normally
      // The timer should count down from 10 minutes from when the ticket was first opened (alerted)
      let ticketOpenTime = null;

      if (displayTicket && Array.isArray(displayTicket.events) && displayTicket.events.length > 0) {
        // Find the first event (alerted) to get the ticket opening time
        const firstEvent = displayTicket.events.reduce((a, b) => (new Date(a.event_date) < new Date(b.event_date) ? a : b));
        ticketOpenTime = new Date(firstEvent.event_date);
      } else {
        // Fallback to ticket timestamp if no events
        ticketOpenTime = new Date(displayTicket.timestamp);
      }

      if (ticketOpenTime) {
        // Calculate target time as 10 minutes from when ticket was opened
        const targetTime = new Date(ticketOpenTime.getTime() + 10 * 60 * 1000);
        const now = new Date();
        const diffSeconds = Math.floor((targetTime - now) / 1000);
        const negative = diffSeconds < 0;
        const absSeconds = Math.abs(diffSeconds);

        const hours = Math.floor(absSeconds / 3600);
        const minutes = Math.floor((absSeconds % 3600) / 60);
        const seconds = absSeconds % 60;

        const sign = negative ? '-' : '';
        const timeStr = `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

        setTimerValue(timeStr);
        setIsNegative(negative);
      }
    };

    // Initial updates
    updateCurrentTime();
    updateTimer();

    // Set up intervals
    const timeInterval = setInterval(updateCurrentTime, 1000);
    const timerInterval = setInterval(updateTimer, 1000);

    return () => {
      clearInterval(timeInterval);
      clearInterval(timerInterval);
    };
  }, [displayTicket && displayTicket.events, displayTicket && displayTicket.targetTime, displayTicket && displayTicket.job_number, apiKey, environment, apiResponse]);

  if (!displayTicket) {
    return (
      <div className="ticket-card no-tickets">
      <div className="no-ticket-logo-container">
        <div className="no-ticket-logo-placeholder">
          <img
            className="andonx-logo-placeholder"
            src={logoCircleReverse}
            alt="AndonX Logo"
          />
        </div>
        <div className="no-ticket-description">
          <h3>A Strand Products Production Alert Dashboard</h3>
        </div>
      </div>
      </div>
    );
  }

  const {
    ticket_id,
    workcenter_code,
    job_number,
    status,
    event_count = 0
  } = displayTicket;

  // Determine the status to display: use the most recent event's status if events exist
  let displayStatus = status;
  let displayEventCount = event_count;
  if (displayTicket && Array.isArray(displayTicket.events) && displayTicket.events.length > 0) {
    // Find the event with the latest event_date (not just highest event_sequence)
    const latestEvent = displayTicket.events.reduce((a, b) => (new Date(a.event_date) > new Date(b.event_date) ? a : b));
    displayStatus = latestEvent.status;
    displayEventCount = displayTicket.events.length;
  }
  const isResolved = displayStatus === 'RESOLVED';
  const eventIcons = Array(Math.min(displayEventCount, 4)).fill(true);
  const jobNumber = job_number;
  const ticketNumber = ticket_id;
  const machineName = workcenter_code;

  // Get part details from database (stored with ticket)
  let partNo = displayTicket.part_number || job_number; // fallback to job number if no part_number in db
  let partName = displayTicket.part_name || `Part ${job_number}`; // fallback to job number if no part_name in db

  // Removed excessive logging - part details logging was causing console spam

  // Determine S or C and color for the left cell
  const sideLetter = (workcenter_code[2] && workcenter_code[2].toUpperCase() === 'C') ? 'C' : 'S';
  const sideClass = sideLetter === 'C' ? 'side-c' : 'side-s';

  // Determine which event icon should be highlighted based on current status
  const getEventIconStatus = () => {
    const status = displayStatus.toLowerCase();
    if (status === 'alerted') return 0; // First circle
    if (status === 'acknowledged') return 1; // Second circle
    if (status === 'evaluated') return 2; // Third circle
    if (status === 'resolved' || !displayTicket.is_active) return 3; // Fourth circle (resolved)
    return -1; // No highlight
  };

  const highlightedIconIndex = getEventIconStatus();

  const renderTicketContent = (t) => {
    if (!t) return null;
    const {
      ticket_id,
      workcenter_code,
      job_number,
      status,
      event_count = 0
    } = t;

    let displayStatusLocal = status;
    let displayEventCountLocal = event_count;
    if (t && Array.isArray(t.events) && t.events.length > 0) {
      const latestEvent = t.events.reduce((a, b) => (new Date(a.event_date) > new Date(b.event_date) ? a : b));
      displayStatusLocal = latestEvent.status;
      displayEventCountLocal = t.events.length;
    }
    const isResolvedLocal = displayStatusLocal === 'RESOLVED';
    const eventIconsLocal = Array(Math.min(displayEventCountLocal, 4)).fill(true);
    const jobNumberLocal = job_number;
    const ticketNumberLocal = ticket_id;
    const machineNameLocal = workcenter_code;

    let partNoLocal = t.part_number || job_number;
    let partNameLocal = t.part_name || `Part ${job_number}`;

    const sideLetterLocal = (workcenter_code[2] && workcenter_code[2].toUpperCase() === 'C') ? 'C' : 'S';
    const sideClassLocal = sideLetterLocal === 'C' ? 'side-c' : 'side-s';

    const getEventIconStatusLocal = () => {
      const statusLower = (displayStatusLocal || '').toLowerCase();
      if (statusLower === 'alerted') return 0;
      if (statusLower === 'acknowledged') return 1;
      if (statusLower === 'evaluated') return 2;
      if (statusLower === 'resolved' || !t.is_active) return 3;
      return -1;
    };
    const highlightedIconIndexLocal = getEventIconStatusLocal();

    return (
      <>
        <div className="ticket-card-header">
        <div className={`timer-display ${isNegative ? 'negative' : ''}`}>
          <span className="primary-timer">
            {timerValue}
          </span>
          <div className="current-time">{currentTime}</div>
        </div>
          <div className="ticket-info">
          <div className={`job-number ${isNegative ? 'negative' : ''}`}>{jobNumberLocal}</div>
          <div className={`ticket-number ${isNegative ? 'negative' : ''}`}>{ticketNumberLocal}</div>
        </div>
        </div>
        <div className="ticket-card-body">
        <div className={`side-indicator ${sideClassLocal}`}>
          {sideLetterLocal}
        </div>
        <div className="ticket-content">
          <div className="workcenter">{machineNameLocal}</div>
          <div className="status-row">
            <div className="event-icons">
                                                                      {Array(4).fill(true).map((icon, index) => {
                const isCompleted = index < highlightedIconIndexLocal;
                const isCurrent = index === highlightedIconIndexLocal;
                const isPending = index > highlightedIconIndexLocal;
                const stepNumber = index + 1;

                return (
                  <div key={index} className={`event-icon-container ${isCompleted ? 'completed' : ''} ${isCurrent ? 'highlighted' : ''} ${isPending ? 'pending' : ''}`}>
                    {isCompleted ? (
                      <img
                        src={checkCircleIcon}
                        alt={`Completed Event ${stepNumber}`}
                        className="event-icon"
                      />
                    ) : isCurrent ? (
                      <img
                        src={dropletIcon}
                        alt={`Current Event ${stepNumber}`}
                        className="event-icon"
                      />
                    ) : (
                      <span className="event-icon" />
                    )}
                    <div className="step-number">{stepNumber}</div>
                    {isCurrent && (
                      <div className="icon-status-display">
                        <span className="icon-status-text">
                          {stepNumber === 4 ? 'Resolved' : displayStatusLocal}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="part-info">
            <div className="part-no">{partNoLocal}</div>
            <div className="part-name">{partNameLocal}</div>
          </div>
        </div>
        </div>
      </>
    );
  };

  return (
    <div className="primary-ticket-slider">
      {activeTickets && activeTickets.length > 0 ? (
        <div className="carousel-container">
          <div className="carousel-track" ref={trackRef}>
            {/* Clone last slide at the beginning for infinite loop */}
            {activeTickets.length > 1 && activeTickets[activeTickets.length - 1] && (
              <div key={`clone-last-${activeTickets[activeTickets.length - 1].ticket_id}`} className="ticket-card">
                {renderTicketContent(activeTickets[activeTickets.length - 1])}
              </div>
            )}
            {/* Real slides */}
            {activeTickets.map((t, index) => {
              if (!t || !t.ticket_id) return null;

              return (
                <div key={t.ticket_id} className="ticket-card">
                  {renderTicketContent(t)}
                </div>
              );
            })}
            {/* Clone first slide at the end for infinite loop */}
            {activeTickets.length > 1 && activeTickets[0] && (
              <div key={`clone-first-${activeTickets[0].ticket_id}`} className="ticket-card">
                {renderTicketContent(activeTickets[0])}
              </div>
            )}
          </div>
        </div>
      ) : (
        displayTicket && (
          <div className="ticket-card">
            {renderTicketContent(displayTicket)}
          </div>
        )
      )}
    </div>
  );
};

export default PrimaryTicketDisplay;